import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Shield, Facebook } from "lucide-react";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-stone-950 text-stone-300 border-t border-stone-800 overflow-hidden">
      {/* Background Image with Dark Gradient Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/id3-1_1.jpeg"
          alt="Baggrund"
          className="w-full h-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-950/80 via-stone-950/70 to-stone-950/85" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
          {/* Column 1: Info & Logo */}
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              <img
                src="https://assets.auxodev.dk/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/logo3-carinas-k-c3-b8reskole-orange-outline-j.jpg"
                alt="Carinas Køreskole"
                className="h-14 w-auto object-contain bg-white p-1.5 rounded-lg"
              />
            </Link>
            <p className="text-stone-400 text-sm leading-relaxed">
              Hos Carinas Køreskole i Gug ønsker vi at uddanne gode, trafiksikre bilister, med vægt på sikkerhed og god trafikadfærd. Vi prioriterer små hold med fokus på den enkelte.
            </p>
            <div className="flex items-center gap-4 bg-white/5 p-4 rounded-xl border border-white/10 max-w-sm">
              <img
                src="https://assets.auxodev.dk/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/dansk-20k-c3-b8rel-c3-a6rerunion.png"
                alt="Dansk Kørelærerunion"
                className="h-12 w-auto object-contain shrink-0"
              />
              <p className="text-xs text-stone-300 font-medium">
                Carinas Køreskole er medlem af <strong className="text-white block">Dansk Kørelærer-Union</strong>
              </p>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className="text-white text-base font-bold uppercase tracking-wider mb-6">Navigation</h3>
            <ul className="space-y-3.5 text-sm">
              <li>
                <Link to="/" className="hover:text-brand-secondary transition-colors duration-150">Velkommen</Link>
              </li>
              <li>
                <Link to="/korekort" className="hover:text-brand-secondary transition-colors duration-150">Kørekort til personbil</Link>
              </li>
              <li>
                <Link to="/priser" className="hover:text-brand-secondary transition-colors duration-150">Priser & rater</Link>
              </li>
              <li>
                <Link to="/teori" className="hover:text-brand-secondary transition-colors duration-150">Teoriundervisning</Link>
              </li>
              <li>
                <Link to="/koreteknisk-anlaeg" className="hover:text-brand-secondary transition-colors duration-150">Køreteknisk anlæg</Link>
              </li>
              <li>
                <Link to="/forstehjaelp" className="hover:text-brand-secondary transition-colors duration-150">Førstehjælp</Link>
              </li>
              <li>
                <Link to="/om-carina" className="hover:text-brand-secondary transition-colors duration-150">Bilen og kørelæreren</Link>
              </li>
              <li>
                <Link to="/kontakt" className="hover:text-brand-secondary transition-colors duration-150">Holdstart & tilmelding</Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Contact & Location */}
          <div className="space-y-6">
            <h3 className="text-white text-base font-bold uppercase tracking-wider">Kontakt & Adresse</h3>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-brand-secondary shrink-0 mt-0.5" />
                <div>
                  <span className="text-stone-400 block text-xs">Telefon</span>
                  <a href="tel:30517269" className="text-white hover:text-brand-secondary font-semibold transition-colors">30 51 72 69</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-brand-secondary shrink-0 mt-0.5" />
                <div>
                  <span className="text-stone-400 block text-xs">E-mail</span>
                  <a href="mailto:carinaskoereskole@gmail.com" className="text-white hover:text-brand-secondary font-semibold transition-colors">carinaskoereskole@gmail.com</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-brand-secondary shrink-0 mt-0.5" />
                <div>
                  <span className="text-stone-400 block text-xs">Teorilokale</span>
                  <p className="text-white font-medium">Gugvej 186 D, kld. tv.<br />9210 Aalborg SØ</p>
                  <span className="text-stone-400 text-xs block mt-1">(Bus nr. 15 holder lige ved døren)</span>
                </div>
              </li>
            </ul>
            <div className="pt-2">
              <a
                href="https://www.facebook.com/carinaskoereskole.dk/?locale=da_DK"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors duration-150 shadow-md w-full sm:w-auto justify-center"
              >
                <Facebook className="h-4.5 w-4.5" />
                Følg os på Facebook
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-stone-800 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-stone-500">
          <p>© {currentYear} Carinas Køreskole. Alle rettigheder forbeholdes. CVR: 40126996</p>
          <div className="flex items-center gap-4">
            <a
              href="https://www.facebook.com/carinaskoereskole.dk/?locale=da_DK"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-stone-300 inline-flex items-center gap-1 transition-colors"
            >
              <Facebook className="h-3.5 w-3.5" />
              Facebook
            </a>
            <span className="text-stone-700">|</span>
            <Link to="/persondata" className="inline-flex items-center gap-1 hover:text-stone-300 transition-colors">
              <Shield className="h-3.5 w-3.5" />
              Persondata (GDPR)
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
